import { useEffect, useState } from 'react'
import { Box, Chip, LinearProgress, Paper, Stack, Typography } from '@mui/material'
import { FunctionsHttpError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { useFiltros } from '../state/FiltrosContext'

export type No = { tipo: 'raiz' | 'fator' | 'onde' | 'hipotese' | 'meta' | 'acao'; texto: string; destaque?: boolean; filhos?: No[] }
type Diagnostico = { situacao: 'sem_dados' | 'sem_conversoes' | 'sem_comparacao' | 'estavel' | 'subiu' | 'caiu'; resumo: string; arvore: No[] }

const SITUACAO: Record<Diagnostico['situacao'], { texto: string; cor: 'default' | 'warning' | 'success' | 'info' }> = {
  subiu: { texto: 'Custo subiu', cor: 'warning' },
  caiu: { texto: 'Custo caiu', cor: 'success' },
  estavel: { texto: 'Estável', cor: 'default' },
  sem_comparacao: { texto: 'Sem comparação', cor: 'info' },
  sem_conversoes: { texto: 'Sem conversões', cor: 'warning' },
  sem_dados: { texto: 'Sem dados', cor: 'default' },
}
const ROTULO: Partial<Record<No['tipo'], string>> = { meta: 'Meta', hipotese: 'Causa provável', acao: 'O que fazer' }

/**
 * O porquê do custo por conversão, em árvore: quanto veio do CPM, do CTR e da conversão dos
 * cliques, onde o custo a mais se concentrou e a causa provável. Tudo do motor (sem IA): a
 * conta é exata e os números são os do painel.
 */
export function DiagnosticoCusto({ plataforma, campanhaId }: { plataforma?: string; campanhaId?: string }) {
  const { intervalo, marcaId } = useFiltros()
  const [d, setD] = useState<Diagnostico | null>(null)
  const [erro, setErro] = useState<string | null>(null)

  useEffect(() => {
    if (!marcaId) return
    let cancelado = false
    setD(null)
    setErro(null)
    supabase.functions
      .invoke('leitura-ia', {
        body: { analise: 'diagnostico', inicio: intervalo.inicio, fim: intervalo.fim, marca_id: marcaId, plataforma: plataforma || null, campanha_id: campanhaId || null },
      })
      .then(async ({ data, error }) => {
        if (cancelado) return
        if (error) {
          const detalhe = error instanceof FunctionsHttpError ? await error.context.json().catch(() => null) : null
          setErro(detalhe?.erro ?? error.message)
        } else setD(data as Diagnostico)
      })
    return () => {
      cancelado = true
    }
  }, [intervalo, marcaId, plataforma, campanhaId])

  const titulo = (
    <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Outfit", sans-serif', mr: 'auto' }}>
      Diagnóstico do custo por conversão
    </Typography>
  )
  if (!marcaId) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        {titulo}
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Escolha uma marca: o custo por conversão só se compara dentro da mesma marca.
        </Typography>
      </Paper>
    )
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        {titulo}
        {d ? <Chip size="small" variant="outlined" color={SITUACAO[d.situacao].cor} label={SITUACAO[d.situacao].texto} /> : null}
      </Stack>
      {erro ? (
        <Typography variant="body2" color="error">
          Diagnóstico indisponível: {erro}
        </Typography>
      ) : !d ? (
        <LinearProgress sx={{ borderRadius: 1 }} />
      ) : (
        <Stack spacing={1.25}>
          <ArvoreDiagnostico arvore={d.arvore} />
          {d.situacao === 'subiu' || d.situacao === 'caiu' ? (
            <Typography variant="caption" color="text.secondary">
              Custo por conversão = CPM ÷ (CTR × taxa de conversão dos cliques): a variação se divide exatamente entre os três. "Custo a mais" é o que
              o item gastou além do que gastaria com o custo por conversão que tinha no período anterior.
            </Typography>
          ) : null}
        </Stack>
      )}
    </Paper>
  )
}

/** A árvore do diagnóstico (também no resumo e no relatório mensal). */
export function ArvoreDiagnostico({ arvore }: { arvore: No[] }) {
  return (
    <>
      {arvore.map((n, i) => (
        <Box key={`${n.tipo}-${i}`}>
          {n.tipo === 'raiz' ? (
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {n.texto}
            </Typography>
          ) : n.filhos?.length ? (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.6 }}>
                {n.texto}
              </Typography>
              <Box component="ul" sx={{ m: 0, pl: 2.25 }}>
                {n.filhos.map((f) => (
                  <Typography component="li" variant="body2" key={f.texto} sx={{ mb: 0.5, fontWeight: f.destaque ? 600 : 400 }}>
                    {f.texto}
                  </Typography>
                ))}
              </Box>
            </>
          ) : (
            <Typography variant="body2">
              {ROTULO[n.tipo] ? (
                <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
                  {ROTULO[n.tipo]}:{' '}
                </Typography>
              ) : null}
              {n.texto}
            </Typography>
          )}
        </Box>
      ))}
    </>
  )
}
